import { useState, useEffect } from 'react'
import { Card, Row, Col, Form, Button, Modal, Alert, Spinner, Badge } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { systemApi } from '../services/api'
import api from '../services/api'

interface TwoFaStatus {
  enabled: boolean
  setup_pending: boolean
}

interface TwoFaSetup {
  secret: string
  qr_code: string
  provisioning_uri: string
}

interface TrustedIpsData {
  enabled: boolean
  ips: string[]
  current_ip: string
}

export default function Settings() {
  const [status, setStatus] = useState<{ status: string; version: string } | null>(null)
  const [reloading, setReloading] = useState(false)

  // 2FA state
  const [twoFaStatus, setTwoFaStatus] = useState<TwoFaStatus>({ enabled: false, setup_pending: false })
  const [twoFaSetup, setTwoFaSetup] = useState<TwoFaSetup | null>(null)
  const [twoFaCode, setTwoFaCode] = useState('')
  const [twoFaLoading, setTwoFaLoading] = useState(false)
  const [show2faModal, setShow2faModal] = useState(false)
  const [show2faDisableModal, setShow2faDisableModal] = useState(false)
  const [disableCode, setDisableCode] = useState('')

  // Password change state
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordLoading, setPasswordLoading] = useState(false)

  // Trusted IPs state
  const [trustedIps, setTrustedIps] = useState<TrustedIpsData>({ enabled: false, ips: [], current_ip: '' })
  const [newTrustedIp, setNewTrustedIp] = useState('')
  const [trustedIpsLoading, setTrustedIpsLoading] = useState(false)

  useEffect(() => {
    systemApi.status().then(res => setStatus(res.data))
    // Load 2FA status
    load2faStatus()
    // Load trusted IPs
    loadTrustedIps()
  }, [])

  const load2faStatus = async () => {
    try {
      const res = await api.get('/auth/2fa/status')
      setTwoFaStatus(res.data)
    } catch {
      console.error('Failed to load 2FA status')
    }
  }

  const loadTrustedIps = async () => {
    try {
      const res = await api.get('/auth/trusted-ips')
      setTrustedIps(res.data)
    } catch {
      console.error('Failed to load trusted IPs')
    }
  }

  const toggleTrustedIps = async (enabled: boolean) => {
    setTrustedIpsLoading(true)
    try {
      await api.put('/auth/trusted-ips', { enabled, ips: trustedIps.ips })
      setTrustedIps({ ...trustedIps, enabled })
      toast.success(enabled ? 'IP-Authentifizierung aktiviert' : 'IP-Authentifizierung deaktiviert')
    } catch {
      toast.error('Fehler beim Speichern')
    } finally {
      setTrustedIpsLoading(false)
    }
  }

  const addCurrentIp = async () => {
    setTrustedIpsLoading(true)
    try {
      const res = await api.post('/auth/trusted-ips/add-current')
      toast.success(res.data.message)
      loadTrustedIps()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Fehler beim Hinzufügen')
    } finally {
      setTrustedIpsLoading(false)
    }
  }

  const addTrustedIp = async () => {
    if (!newTrustedIp.trim()) return
    setTrustedIpsLoading(true)
    try {
      await api.post('/auth/trusted-ips/add', { ip: newTrustedIp.trim() })
      toast.success(`IP ${newTrustedIp} hinzugefügt`)
      setNewTrustedIp('')
      loadTrustedIps()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Fehler beim Hinzufügen')
    } finally {
      setTrustedIpsLoading(false)
    }
  }

  const removeTrustedIp = async (ip: string) => {
    if (!window.confirm(`IP ${ip} wirklich entfernen?`)) return
    setTrustedIpsLoading(true)
    try {
      await api.delete(`/auth/trusted-ips/${encodeURIComponent(ip)}`)
      toast.success(`IP ${ip} entfernt`)
      loadTrustedIps()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Fehler beim Entfernen')
    } finally {
      setTrustedIpsLoading(false)
    }
  }

  const start2faSetup = async () => {
    setTwoFaLoading(true)
    try {
      const res = await api.post('/auth/2fa/setup')
      setTwoFaSetup(res.data)
      setTwoFaCode('')
      setShow2faModal(true)
    } catch {
      toast.error('Fehler beim Starten der 2FA-Einrichtung')
    } finally {
      setTwoFaLoading(false)
    }
  }

  const verify2faSetup = async () => {
    if (twoFaCode.length !== 6) {
      toast.error('Bitte geben Sie den 6-stelligen Code ein')
      return
    }

    setTwoFaLoading(true)
    try {
      await api.post('/auth/2fa/verify', { code: twoFaCode })
      toast.success('2FA erfolgreich aktiviert')
      setShow2faModal(false)
      setTwoFaSetup(null)
      setTwoFaCode('')
      load2faStatus()
    } catch {
      toast.error('Ungültiger Verifizierungscode')
    } finally {
      setTwoFaLoading(false)
    }
  }

  const disable2fa = async () => {
    if (disableCode.length !== 6) {
      toast.error('Bitte geben Sie den 6-stelligen Code ein')
      return
    }

    setTwoFaLoading(true)
    try {
      await api.post('/auth/2fa/disable', { code: disableCode })
      toast.success('2FA deaktiviert')
      setShow2faDisableModal(false)
      setDisableCode('')
      load2faStatus()
    } catch {
      toast.error('Ungültiger Verifizierungscode')
    } finally {
      setTwoFaLoading(false)
    }
  }

  const handleReload = async () => {
    setReloading(true)
    try {
      await systemApi.reload()
      toast.success('Asterisk Konfiguration neu geladen')
    } catch { toast.error('Fehler beim Neuladen') }
    finally { setReloading(false) }
  }

  const handlePasswordChange = async () => {
    if (newPassword !== confirmPassword) {
      toast.error('Die Passwörter stimmen nicht überein')
      return
    }
    if (newPassword.length < 8) {
      toast.error('Das Passwort muss mindestens 8 Zeichen lang sein')
      return
    }

    setPasswordLoading(true)
    try {
      await api.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword
      })
      toast.success('Passwort erfolgreich geändert')
      setShowPasswordModal(false)
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    } catch (error: any) {
      const detail = error.response?.data?.detail || 'Fehler beim Ändern des Passworts'
      toast.error(detail)
    } finally {
      setPasswordLoading(false)
    }
  }

  return (
    <div>
      <h4 className="mb-4">Einstellungen</h4>

      <Row>
        <Col md={6}>
          <Card className="mb-4">
            <Card.Header><i className="bi bi-info-circle me-2"></i>System Status</Card.Header>
            <Card.Body>
              {status && (
                <div>
                  <div className="mb-2"><strong>Status:</strong> <span className="text-success">{status.status}</span></div>
                  <div className="mb-2"><strong>Version:</strong> {status.version}</div>
                </div>
              )}
            </Card.Body>
          </Card>

          <Card className="mb-4">
            <Card.Header><i className="bi bi-arrow-repeat me-2"></i>Asterisk Konfiguration</Card.Header>
            <Card.Body>
              <p className="text-muted">Lädt alle Konfigurationsdateien neu und wendet Änderungen an.</p>
              <Button variant="primary" onClick={handleReload} disabled={reloading}>
                {reloading ? 'Lade neu...' : 'Konfiguration neu laden'}
              </Button>
            </Card.Body>
          </Card>

          <Card className="mb-4">
            <Card.Header><i className="bi bi-key me-2"></i>Passwort ändern</Card.Header>
            <Card.Body>
              <p className="text-muted">
                Ändern Sie Ihr Passwort regelmäßig, um die Sicherheit Ihres Kontos zu gewährleisten.
              </p>
              <Button variant="primary" onClick={() => setShowPasswordModal(true)}>
                <i className="bi bi-key me-2"></i>Passwort ändern
              </Button>
            </Card.Body>
          </Card>

          <Card className="mb-4">
            <Card.Header><i className="bi bi-shield-lock me-2"></i>2-Faktor-Authentifizierung</Card.Header>
            <Card.Body>
              {twoFaStatus.enabled ? (
                <>
                  <div className="d-flex align-items-center mb-3">
                    <span className="badge bg-success me-2">
                      <i className="bi bi-check-circle me-1"></i>Aktiv
                    </span>
                    <span className="text-muted">2FA ist für Ihr Konto aktiviert</span>
                  </div>
                  <Button
                    variant="outline-danger"
                    onClick={() => setShow2faDisableModal(true)}
                    disabled={twoFaLoading}
                  >
                    <i className="bi bi-shield-x me-2"></i>2FA deaktivieren
                  </Button>
                </>
              ) : (
                <>
                  <p className="text-muted">
                    Schützen Sie Ihr Konto mit einem zusätzlichen Sicherheitsfaktor.
                    Verwenden Sie eine Authenticator-App wie Google Authenticator, Authy oder Microsoft Authenticator.
                  </p>
                  <Button
                    variant="primary"
                    onClick={start2faSetup}
                    disabled={twoFaLoading}
                  >
                    {twoFaLoading ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Wird vorbereitet...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-shield-plus me-2"></i>2FA aktivieren
                      </>
                    )}
                  </Button>
                </>
              )}
            </Card.Body>
          </Card>

          <Card className="mb-4">
            <Card.Header><i className="bi bi-geo-alt me-2"></i>Vertrauenswürdige IPs</Card.Header>
            <Card.Body>
              <p className="text-muted">
                Bei Login von einer vertrauenswürdigen IP wird 2FA übersprungen.
              </p>

              <Form.Check
                type="switch"
                id="ip-auth-enabled"
                label="IP-basierte Authentifizierung aktivieren"
                checked={trustedIps.enabled}
                onChange={(e) => toggleTrustedIps(e.target.checked)}
                disabled={trustedIpsLoading}
                className="mb-3"
              />

              {trustedIps.enabled && (
                <>
                  <Alert variant="info" className="py-2" style={{ fontSize: '0.85rem' }}>
                    <i className="bi bi-info-circle me-2"></i>
                    Ihre aktuelle IP: <code>{trustedIps.current_ip}</code>
                    {!trustedIps.ips.includes(trustedIps.current_ip) && (
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 ms-2"
                        onClick={addCurrentIp}
                        disabled={trustedIpsLoading}
                      >
                        Hinzufügen
                      </Button>
                    )}
                  </Alert>

                  <Form className="mb-3" onSubmit={(e) => { e.preventDefault(); addTrustedIp(); }}>
                    <div className="d-flex gap-2">
                      <Form.Control
                        type="text"
                        placeholder="IP oder CIDR (z.B. 192.168.1.0/24)"
                        value={newTrustedIp}
                        onChange={(e) => setNewTrustedIp(e.target.value)}
                        size="sm"
                      />
                      <Button
                        variant="success"
                        size="sm"
                        onClick={addTrustedIp}
                        disabled={trustedIpsLoading || !newTrustedIp.trim()}
                      >
                        <i className="bi bi-plus-lg"></i>
                      </Button>
                    </div>
                  </Form>

                  {trustedIps.ips.length > 0 ? (
                    <div className="d-flex flex-wrap gap-2">
                      {trustedIps.ips.map(ip => (
                        <Badge
                          key={ip}
                          bg="secondary"
                          className="d-flex align-items-center gap-1 py-2 px-2"
                        >
                          <code className="text-white">{ip}</code>
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 text-white"
                            onClick={() => removeTrustedIp(ip)}
                            disabled={trustedIpsLoading}
                            style={{ lineHeight: 1 }}
                          >
                            <i className="bi bi-x"></i>
                          </Button>
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <small className="text-muted">Keine vertrauenswürdigen IPs konfiguriert.</small>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="mb-4">
            <Card.Header><i className="bi bi-gear me-2"></i>Allgemeine Einstellungen</Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>Zeitzone</Form.Label>
                <Form.Select defaultValue="Europe/Berlin">
                  <option value="Europe/Berlin">Europe/Berlin</option>
                  <option value="Europe/Vienna">Europe/Vienna</option>
                  <option value="Europe/Zurich">Europe/Zurich</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Sprache</Form.Label>
                <Form.Select defaultValue="de">
                  <option value="de">Deutsch</option>
                  <option value="en">English</option>
                </Form.Select>
              </Form.Group>
              <Button variant="primary">Speichern</Button>
            </Card.Body>
          </Card>

        </Col>
      </Row>

      {/* 2FA Setup Modal */}
      <Modal show={show2faModal} onHide={() => setShow2faModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-shield-lock me-2"></i>
            2FA einrichten
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {twoFaSetup && (
            <>
              <p className="text-muted mb-3">
                Scannen Sie den QR-Code mit Ihrer Authenticator-App
              </p>
              <div className="mb-3">
                <img
                  src={twoFaSetup.qr_code}
                  alt="2FA QR Code"
                  style={{ maxWidth: '200px', border: '1px solid #dee2e6', borderRadius: '8px' }}
                />
              </div>
              <Alert variant="secondary" className="text-start" style={{ fontSize: '0.85rem' }}>
                <strong>Manueller Code:</strong>
                <code className="d-block mt-1 p-2 bg-light rounded" style={{ wordBreak: 'break-all' }}>
                  {twoFaSetup.secret}
                </code>
              </Alert>
              <Form.Group className="mt-3">
                <Form.Label>Verifizierungscode eingeben</Form.Label>
                <Form.Control
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={twoFaCode}
                  onChange={(e) => setTwoFaCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  className="text-center fs-4"
                  style={{ letterSpacing: '0.3em' }}
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShow2faModal(false)}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            onClick={verify2faSetup}
            disabled={twoFaLoading || twoFaCode.length !== 6}
          >
            {twoFaLoading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Verifiziere...
              </>
            ) : (
              <>
                <i className="bi bi-check-lg me-2"></i>
                Aktivieren
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 2FA Disable Modal */}
      <Modal show={show2faDisableModal} onHide={() => setShow2faDisableModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-shield-x me-2"></i>
            2FA deaktivieren
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            <i className="bi bi-exclamation-triangle me-2"></i>
            <strong>Warnung:</strong> Durch das Deaktivieren von 2FA wird Ihr Konto weniger sicher.
          </Alert>
          <Form.Group>
            <Form.Label>Geben Sie Ihren aktuellen 2FA-Code ein, um fortzufahren</Form.Label>
            <Form.Control
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={disableCode}
              onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ''))}
              placeholder="000000"
              className="text-center fs-4"
              style={{ letterSpacing: '0.3em' }}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShow2faDisableModal(false)}>
            Abbrechen
          </Button>
          <Button
            variant="danger"
            onClick={disable2fa}
            disabled={twoFaLoading || disableCode.length !== 6}
          >
            {twoFaLoading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Deaktiviere...
              </>
            ) : (
              <>
                <i className="bi bi-shield-x me-2"></i>
                Deaktivieren
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Password Change Modal */}
      <Modal show={showPasswordModal} onHide={() => setShowPasswordModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-key me-2"></i>
            Passwort ändern
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Aktuelles Passwort</Form.Label>
            <Form.Control
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Ihr aktuelles Passwort"
              autoComplete="current-password"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Neues Passwort</Form.Label>
            <Form.Control
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Mindestens 8 Zeichen"
              autoComplete="new-password"
            />
            <Form.Text className="text-muted">
              Das Passwort muss mindestens 8 Zeichen lang sein.
            </Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Neues Passwort bestätigen</Form.Label>
            <Form.Control
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Passwort wiederholen"
              autoComplete="new-password"
              isInvalid={confirmPassword.length > 0 && newPassword !== confirmPassword}
            />
            {confirmPassword.length > 0 && newPassword !== confirmPassword && (
              <Form.Control.Feedback type="invalid">
                Die Passwörter stimmen nicht überein.
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowPasswordModal(false)
            setCurrentPassword('')
            setNewPassword('')
            setConfirmPassword('')
          }}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            onClick={handlePasswordChange}
            disabled={passwordLoading || !currentPassword || newPassword.length < 8 || newPassword !== confirmPassword}
          >
            {passwordLoading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Speichern...
              </>
            ) : (
              <>
                <i className="bi bi-check-lg me-2"></i>
                Passwort ändern
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
